import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Plus, Trash2, Tag, Calendar, Clock } from "lucide-react";
import CouponField from "../components/CouponField";
import { trackInitiateCheckout } from "../lib/metaPixel";

const money = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\.00$/, "")}`;
const prettyDate = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return y ? new Date(y, m - 1, d, 12).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "";
};

function CampOption({ c, checked, onToggle }) {
  const full = c.spots_left <= 0;
  return (
    <button type="button" data-testid={`camp-option-${c.id}`} onClick={() => !full && onToggle(c.id)} disabled={full}
      className={`w-full text-left flex gap-4 p-4 border-2 transition-colors ${checked ? "border-[#121212] bg-[#C6FF33]" : "border-[#121212]/20 bg-white hover:border-[#121212]"} ${full ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`mt-1 w-6 h-6 shrink-0 border-2 border-[#121212] flex items-center justify-center ${checked ? "bg-[#121212] text-[#C6FF33]" : "bg-white"}`}>
        {checked && <Check size={15} strokeWidth={3} />}
      </span>
      <img src={thumb(c.image_url, 200)} alt={c.title} className="w-16 h-16 object-cover border-2 border-[#121212] shrink-0 hidden sm:block" />
      <span className="flex-1 min-w-0">
        <span className="block font-serif font-black text-xl text-[#121212] leading-tight">{c.title}</span>
        <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {prettyDate(c.date)}</span>
          {c.time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {c.time}{c.duration ? ` · ${c.duration}` : ""}</span>}
        </span>
        <span data-testid={`camp-spots-${c.id}`} className={`block mt-1 text-xs font-bold ${full ? "text-[#E0218A]" : "text-[#121212]"}`}>
          {full ? "Full" : `${c.spots_left} spot${c.spots_left === 1 ? "" : "s"} left`}
        </span>
      </span>
      <span className="text-sm font-bold text-[#121212] whitespace-nowrap">{money(c.price)}<span className="block text-[10px] font-normal text-stone-500">per child</span></span>
    </button>
  );
}

function PriceTiers({ b }) {
  const single = b.camps[0]?.price || 0;
  const tiers = [["1 camp", money(single)]];
  if (b.two_camp_price) tiers.push(["Any 2 camps", money(b.two_camp_price)]);
  if (b.all_camp_price) tiers.push([`All ${b.camp_ids.length} camps`, money(b.all_camp_price)]);
  return (
    <div data-testid="price-tiers" className="mt-8 grid grid-cols-3 border-2 border-[#121212]">
      {tiers.map(([l, v], i) => (
        <div key={l} className={`p-4 ${i ? "border-l-2 border-[#121212]" : ""} ${i === tiers.length - 1 && i > 0 ? "bg-[#E0218A] text-white" : "bg-[#F5EFE6] text-[#121212]"}`}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-80">{l}</p>
          <p className="font-serif font-black text-2xl sm:text-3xl mt-1 whitespace-nowrap">{v}</p>
          <p className="text-[10px] opacity-70">per child</p>
        </div>
      ))}
    </div>
  );
}

function KidsForm({ kids, setKids, maxKids, phone, setPhone }) {
  const update = (i, f, v) => setKids(kids.map((k, j) => (j === i ? { ...k, [f]: v } : k)));
  return (
    <div data-testid="camp-week-kids-form" className="border-2 border-[#121212] p-5">
      <p className="font-serif font-black text-xl text-[#121212]">Who's attending?</p>
      <p className="text-xs text-stone-500 mt-1">Each child is registered for every camp you picked.</p>
      <div className="mt-4 space-y-3">
        {kids.map((k, i) => (
          <div key={i} data-testid={`cw-child-row-${i}`} className="border-2 border-stone-200 p-3">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]">Child {i + 1}</p>
              {kids.length > 1 && <button data-testid={`cw-remove-child-${i}`} onClick={() => setKids(kids.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-500"><Trash2 size={16} /></button>}
            </div>
            <input data-testid={`cw-child-name-${i}`} value={k.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Child's full name"
              className="w-full mt-2 px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none" />
            <input data-testid={`cw-child-allergies-${i}`} value={k.allergies} onChange={(e) => update(i, "allergies", e.target.value)} placeholder="Allergies or dietary needs (optional)"
              className="w-full mt-2 px-3 py-2 border-2 border-stone-200 bg-white focus:outline-none text-sm" />
          </div>
        ))}
      </div>
      <button data-testid="cw-add-child-btn" onClick={() => setKids([...kids, { name: "", allergies: "" }])} disabled={kids.length >= maxKids}
        className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#121212] disabled:opacity-40">
        <Plus size={14} /> Add another child
      </button>
      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212] block mt-4 mb-2">Parent cell phone</label>
      <input data-testid="cw-parent-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567"
        className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none" />
    </div>
  );
}

function Summary({ q, couponDiscount, coupon, total }) {
  if (!q) return null;
  const label = q.applied === "multi_camp" ? `${q.num_camps}-camp price` : q.applied === "sibling" ? "Sibling price" : null;
  const discount = Math.round((q.regular_total - q.base_total) * 100) / 100;
  const totalSave = Math.round((q.savings + couponDiscount) * 100) / 100;
  return (
    <div data-testid="camp-week-summary" className="border-2 border-[#121212] bg-[#F5EFE6] p-5 text-sm text-[#121212]">
      <div className="flex justify-between"><span>{q.num_camps} camp{q.num_camps > 1 ? "s" : ""} × {q.num_children} {q.num_children > 1 ? "children" : "child"}</span><span data-testid="cw-regular-total">{money(q.regular_total)}</span></div>
      {label && discount > 0 && <div className="flex justify-between mt-1 text-[#E0218A] font-bold"><span className="inline-flex items-center gap-1"><Tag size={13} /> {label}</span><span data-testid="cw-discount">−{money(discount)}</span></div>}
      {q.member_discount > 0 && <div className="flex justify-between mt-1 text-[#E0218A] font-bold"><span>Member 15% off</span><span data-testid="cw-member-discount">−{money(q.member_discount)}</span></div>}
      {couponDiscount > 0 && <div className="flex justify-between mt-1"><span>Coupon {coupon.code}</span><span>−{money(couponDiscount)}</span></div>}
      <div className="flex justify-between mt-3 pt-3 border-t-2 border-[#121212] font-serif font-black text-3xl"><span>Total</span><span data-testid="cw-total">{money(total)}</span></div>
      <p className="text-[11px] text-stone-500 mt-1">Sales tax, if any, is added at checkout.</p>
      {totalSave > 0 && <p data-testid="cw-savings" className="mt-3 inline-block bg-[#C6FF33] border-2 border-[#121212] px-3 py-1.5 font-bold">You save {money(totalSave)}!</p>}
      {q.applied === "sibling" && q.multi_camp_total != null && <p className="text-[11px] text-stone-500 mt-2">We applied the sibling price because it's lower for your family. Discounts don't combine.</p>}
    </div>
  );
}

export default function CampWeek() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [missing, setMissing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [kids, setKids] = useState([{ name: "", allergies: "" }]);
  const [phone, setPhone] = useState("");
  const [guest, setGuest] = useState({ name: "", email: "" });
  const [giftCode, setGiftCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/camp-bundles/${slug}`).then(({ data }) => {
      setB(data);
      setPicked(data.camps.filter((c) => c.spots_left > 0).map((c) => c.id));
    }).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    if (!b || !picked.length) { setQ(null); return; }
    api.post(`/camp-bundles/${b.id}/quote`, { camp_ids: picked, num_children: kids.length })
      .then(({ data }) => setQ(data)).catch((e) => { setQ(null); toast.error(formatApiError(e.response?.data?.detail)); });
  }, [b, picked, kids.length, user]);

  if (missing) return <div data-testid="camp-week-missing" className="pt-40 px-6 text-center text-stone-600">This camp week isn't open for registration yet.</div>;
  if (!b) return <div className="pt-32 px-6 text-stone-500">Loading…</div>;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const sel = b.camps.filter((c) => picked.includes(c.id));
  const maxKids = sel.length ? Math.min(...sel.map((c) => c.spots_left)) : 1;
  const amount = q?.amount || 0;
  const couponDiscount = coupon ? (coupon.discount_type === "percent" ? Math.round(amount * coupon.discount_value) / 100 : Math.min(coupon.discount_value, amount)) : 0;
  const total = Math.max(0, Math.round((amount - couponDiscount) * 100) / 100);
  const isGuest = !user || user === false;

  const checkout = async () => {
    if (!picked.length) return toast.error("Please choose at least one camp.");
    if (kids.some((k) => !k.name.trim())) return toast.error("Please enter a name for each child.");
    if (!phone.trim()) return toast.error("Please enter a parent cell phone number.");
    if (isGuest && !guest.name.trim()) return toast.error("Please enter your name.");
    if (isGuest && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guest.email.trim())) return toast.error("Please enter a valid email address.");
    if (!agreed) return toast.error("Please agree to the cancellation & transfer policy to continue.");
    setBusy(true);
    trackInitiateCheckout({ id: b.id, name: b.title, value: total, numItems: kids.length * picked.length });
    try {
      const payload = { item_type: "camp_bundle", item_id: b.id, origin_url: window.location.origin, camp_ids: picked,
        parent_phone: phone.trim(), children: kids.map((k) => ({ name: k.name.trim(), allergies: k.allergies.trim() })) };
      if (giftCode.trim()) payload.gift_code = giftCode.trim().toUpperCase();
      if (coupon?.code) payload.coupon_code = coupon.code;
      if (isGuest) { payload.guest_name = guest.name.trim(); payload.guest_email = guest.email.trim(); }
      const { data } = await api.post("/payments/checkout", payload);
      if (data.free && data.session_id) { nav(`/payment/success?session_id=${data.session_id}`); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div data-testid="camp-week-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16">
        <div className="lg:sticky lg:top-28 self-start">
          <div className="aspect-square overflow-hidden border-2 border-[#121212]">
            <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />
          </div>
          <p className="mt-8 text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">Kids Camp Week</p>
          <h1 data-testid="camp-week-title" className="font-serif font-black text-4xl sm:text-5xl text-[#121212] mt-3 leading-[1.05]">{b.title}</h1>
          {b.description && <p className="mt-5 text-stone-600 leading-relaxed">{b.description}</p>}
          <PriceTiers b={b} />
          <p className="mt-3 text-xs text-stone-500">Siblings: we automatically use the sibling rate or the multi-camp price — whichever is lower for your family.</p>
        </div>
        <div className="space-y-6">
          <div>
            <p className="font-serif font-black text-2xl text-[#121212]">Pick your camps</p>
            <div className="mt-4 space-y-3">{b.camps.map((c) => <CampOption key={c.id} c={c} checked={picked.includes(c.id)} onToggle={toggle} />)}</div>
            {!b.camps.length && <p className="text-stone-500 text-sm mt-3">No camps are open for this week yet.</p>}
          </div>
          {picked.length > 0 && <>
            <KidsForm kids={kids} setKids={setKids} maxKids={maxKids} phone={phone} setPhone={setPhone} />
            <input data-testid="cw-gift-code" value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="Have a gift code? GIFT-XXXX-XXXX"
              className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none uppercase font-mono text-sm" />
            <CouponField amount={amount} applied={coupon} onChange={setCoupon} context="classes" testid="cw-coupon" />
            {isGuest && (
              <div data-testid="cw-guest-details" className="grid sm:grid-cols-2 gap-2">
                <input data-testid="cw-guest-name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Your name" className="px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
                <input data-testid="cw-guest-email" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Your email" className="px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
              </div>
            )}
            <Summary q={q} couponDiscount={couponDiscount} coupon={coupon} total={total} />
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="cw-policy-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#121212] shrink-0" />
              <span className="text-sm text-[#121212] leading-snug">I understand that camp registrations are nonrefundable and that transfers require at least 24 hours' notice.</span>
            </label>
            <button data-testid="cw-checkout-btn" onClick={checkout} disabled={busy || !agreed || !q}
              className="w-full px-10 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-xs tracking-[0.25em] uppercase font-bold transition-colors">
              {busy ? "Loading…" : `Reserve ${picked.length} camp${picked.length > 1 ? "s" : ""}`}
            </button>
          </>}
        </div>
      </div>
    </div>
  );
}
